import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { playerAPI } from '../../../services/api';
import { apiErrorMessage, pickIsoDate, unwrapApiBody } from '../../../utils/apiResponse';

const Box = styled.div`
  margin-top: 0.5rem;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
`;

const Note = styled.p`
  margin: 0 0 1rem;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #475569;
`;

const Field = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #043663;
`;

const DateInput = styled.input`
  display: block;
  width: 100%;
  max-width: 220px;
  margin-top: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PrimaryBtn = styled.button`
  padding: 0.65rem 1.1rem;
  border: none;
  border-radius: 8px;
  background: #043663;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryBtn = styled.button`
  padding: 0.65rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
`;

const Muted = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
`;

const StatusBanner = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e3a5f;
  font-size: 0.9rem;
  line-height: 1.45;
`;

export default function HelpInjuryPeriodPanel() {
  const { t } = useTranslation();
  const [untilDate, setUntilDate] = useState('');
  const [savedUntil, setSavedUntil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = unwrapApiBody(await playerAPI.getInjuryPeriod());
      const u = pickIsoDate(res?.until_date ?? res?.untilDate);
      setSavedUntil(u);
      setUntilDate(u || '');
    } catch (e) {
      console.warn('[injury-period] load failed', e);
      setSavedUntil(null);
      setUntilDate('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!untilDate) {
      toast.error(t('phase2.playerHelpWizard.injuryPeriod.dateRequired'));
      return;
    }
    setSaving(true);
    try {
      const res = unwrapApiBody(await playerAPI.setInjuryPeriod({ until_date: untilDate }));
      if (res?.success !== false) {
        const saved = pickIsoDate(res?.until_date) || untilDate;
        toast.success(
          res?.notified === false
            ? t('phase2.playerHelpWizard.injuryPeriod.updated')
            : t('phase2.playerHelpWizard.injuryPeriod.saved')
        );
        setSavedUntil(saved);
        setUntilDate(saved);
        await load();
      } else {
        toast.error(res?.message || t('phase2.playerHelpWizard.injuryPeriod.saveError'));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t('phase2.playerHelpWizard.injuryPeriod.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await playerAPI.clearInjuryPeriod();
      setUntilDate('');
      setSavedUntil(null);
      toast.success(t('phase2.playerHelpWizard.injuryPeriod.cleared'));
    } catch {
      toast.error(t('phase2.playerHelpWizard.injuryPeriod.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Muted>{t('phase2.playerHelpWizard.injuryPeriod.loading')}</Muted>;
  }

  const saveLabel = savedUntil
    ? t('phase2.playerHelpWizard.injuryPeriod.update')
    : t('phase2.playerHelpWizard.injuryPeriod.save');

  return (
    <Box>
      {savedUntil ? (
        <StatusBanner>
          {t('phase2.playerHelpWizard.injuryPeriod.activeUntil', { date: savedUntil })}
          {' '}
          {t('phase2.playerHelpWizard.injuryPeriod.changeOrRemoveHint')}
        </StatusBanner>
      ) : null}
      <Note>{t('phase2.playerHelpWizard.injuryPeriod.ownDatesNote')}</Note>
      <Note>{t('phase2.playerHelpWizard.injuryPeriod.partnerNote')}</Note>
      <Field>
        {savedUntil
          ? t('phase2.playerHelpWizard.injuryPeriod.changeDateLabel')
          : t('phase2.playerHelpWizard.injuryPeriod.untilLabel')}
        <DateInput
          type="date"
          value={untilDate}
          onChange={(e) => setUntilDate(e.target.value)}
          min={new Date().toISOString().slice(0, 10)}
        />
      </Field>
      <Actions>
        <PrimaryBtn type="button" disabled={saving || !untilDate} onClick={handleSave}>
          {saving ? t('phase2.playerHelpWizard.injuryPeriod.saving') : saveLabel}
        </PrimaryBtn>
        {savedUntil ? (
          <SecondaryBtn type="button" disabled={saving} onClick={handleClear}>
            {t('phase2.playerHelpWizard.injuryPeriod.remove')}
          </SecondaryBtn>
        ) : null}
      </Actions>
    </Box>
  );
}
